import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AuctionCountdown from "components/AuctionCountdown";
import SendNewOfferForm from "components/SendNewOfferForm";
import { getAuctionBids, getAuctionStatus } from "../../api/auction";

const formatIls = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "-";
  return `₪${amount.toLocaleString("he-IL", { maximumFractionDigits: 0 })}`;
};

const formatBidTime = (value) => {
  if (!value) return "";
  const normalized = typeof value === "string" ? value.replace(" ", "T") : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resolveCurrentUserId = (currentUser, offer) => {
  const id = Number(
    currentUser?.id ??
      currentUser?.user_id ??
      currentUser?.userId ??
      offer?.user_id ??
      offer?.userId
  );

  return Number.isFinite(id) && id > 0 ? id : null;
};

const normalizeBidsPayload = (payload) => {
  const bids = Array.isArray(payload?.bids) ? payload.bids : [];
  const totalBids = Number(payload?.summary?.total_bids ?? bids.length) || 0;
  const highestBid = payload?.summary?.highest_bid ?? bids[0]?.bid_amount ?? null;
  const highlightedBid = bids.find((bid) => bid?.is_current_user);
  const currentUserPosition =
    Number(payload?.summary?.current_user_position ?? highlightedBid?.position) || null;

  return {
    bids,
    totalBids,
    highestBid,
    currentUserPosition,
  };
};

const resolveDealerName = (bid, allowRealNames = true) => {
  if (!allowRealNames) return "";

  const name =
    bid?.dealer_name ??
    bid?.dealer_full_name ??
    bid?.dealer_business_name ??
    bid?.business_name ??
    bid?.contact_name ??
    bid?.full_name ??
    bid?.name ??
    bid?.bidder_name;

  return String(name ?? "").trim();
};

const isVisibleAuctionPayload = (payload) => {
  if (!payload) return false;

  const status = String(payload?.status ?? payload?.auction_status ?? "").toLowerCase();
  const isAuction = Number(payload?.is_auction ?? 0) === 1 || payload?.is_auction === true;

  return isAuction && payload?.has_ended !== true && !["ended", "finalized"].includes(status);
};

function MobileAuctionView({
  state,
  loading,
  refreshing,
  error,
  auctionStatus,
  onAuctionStatusUpdate,
  showCurrentUserLabel = true,
}) {
  const positionText = state.currentUserPosition
    ? `${state.currentUserPosition} מתוך ${state.totalBids}`
    : "-";

  return (
    <div className="flex flex-col gap-4 px-3 pb-5 pt-3" dir="rtl">
      {auctionStatus?.is_auction && (
        <div className="rounded-[18px] border border-[#E5E7EB] bg-white px-3 py-2 shadow-[0_8px_20px_rgba(15,23,42,0.08)]">
          <AuctionCountdown
            auctionStatus={auctionStatus}
            onUpdate={onAuctionStatusUpdate}
            variant="topFloating"
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-[14px] border border-[#D9E8FF] bg-[#F3F8FF] px-2 py-2.5 text-center">
          <div className="truncate text-[11px] font-semibold text-[#64748B]">הצעה גבוהה</div>
          <div className="mt-1 truncate text-[15px] font-bold text-[#111827]">{formatIls(state.highestBid)}</div>
        </div>
        <div className="rounded-[14px] border border-[#D9E8FF] bg-[#F3F8FF] px-2 py-2.5 text-center">
          <div className="truncate text-[11px] font-semibold text-[#64748B]">סה"כ הצעות</div>
          <div className="mt-1 truncate text-[15px] font-bold text-[#111827]">{state.totalBids.toLocaleString("he-IL")}</div>
        </div>
        <div className="rounded-[14px] border border-[#E7EAF0] bg-white px-2 py-2.5 text-center">
          <div className="truncate text-[11px] font-semibold text-[#64748B]">המיקום שלך</div>
          <div className="mt-1 truncate text-[15px] font-bold text-[#111827]">{positionText}</div>
        </div>
      </div>

      {refreshing && !loading && (
        <div className="text-center text-[12px] font-medium text-[#64748B]">מעדכנים נתונים...</div>
      )}

      {loading && (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-[74px] animate-pulse rounded-[18px] bg-[#EEF2F7]" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-[18px] border border-[#F7CACA] bg-[#FFF5F5] p-4 text-center text-[14px] font-medium text-[#B42318]">
          {error}
        </div>
      )}

      {!loading && !error && state.bids.length === 0 && (
        <div className="rounded-[20px] border border-dashed border-[#D6DAE2] bg-white p-6 text-center text-[15px] font-medium text-[#6B7280]">
          עדיין לא הוגשו הצעות במכרז
        </div>
      )}

      {!loading && !error && state.bids.length > 0 && (
        <div className="rounded-[20px] border border-[#E4E9F2] bg-white p-3 shadow-[0_10px_26px_rgba(15,23,42,0.07)]">
          <div className="mb-3 flex items-center justify-between border-b border-[#EEF2F7] pb-2">
            <div className="text-[15px] font-bold text-[#111827]">לוח הצעות</div>
            <div className="text-[12px] font-semibold text-[#64748B]">מהגבוה לנמוך</div>
          </div>

          <div className="auction-bids-scroll-list flex max-h-[42vh] flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
            {state.bids.map((bid) => {
              const isCurrentUser = Boolean(bid?.is_current_user);
              const canShowRealNames = !showCurrentUserLabel;
              const dealerName = resolveDealerName(bid, canShowRealNames);
              const bidderLabel = showCurrentUserLabel
                ? bid?.bidder_label || (isCurrentUser ? "ההצעה שלך" : `מציע #${bid.position}`)
                : dealerName || "סוחר";

              return (
                <div
                  key={bid.id}
                  className={`rounded-[16px] border p-3 ${
                    isCurrentUser
                      ? "border-[#CE0000] bg-[#FFF4F4] shadow-[0_12px_26px_rgba(206,0,0,0.13)]"
                      : "border-[#E7EAF0] bg-[#FBFCFE]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[14px] font-bold ${
                          isCurrentUser ? "bg-[#CE0000] text-white" : "bg-[#EEF2F7] text-[#334155]"
                        }`}
                      >
                        {bid.position}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-[15px] font-bold text-[#111827]">
                          {showCurrentUserLabel && isCurrentUser ? "ההצעה שלך" : bidderLabel}
                        </div>
                        <div className="text-[12px] text-[#6B7280]">{formatBidTime(bid.created_at)}</div>
                      </div>
                    </div>
                    <div className="shrink-0 text-left text-[18px] font-bold text-[#111827]" dir="ltr">
                      {formatIls(bid.bid_amount)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SendNewOfferMobilePopup({
  isOpen = true,
  isRejectedOrApproved,
  onClose,
  car,
  currentUser,
  offer,
  onUpdateOffer,
}) {
  const [activeTab, setActiveTab] = useState("offer");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAuctionTab, setShowAuctionTab] = useState(false);
  const [auctionStatusSnapshot, setAuctionStatusSnapshot] = useState(null);
  const [auctionSnapshotLoaded, setAuctionSnapshotLoaded] = useState(false);
  const [hasSubmittedAuctionOffer, setHasSubmittedAuctionOffer] = useState(false);
  const [auctionBidsState, setAuctionBidsState] = useState({
    bids: [],
    highestBid: null,
    totalBids: 0,
    currentUserPosition: null,
  });
  const [auctionBidsLoading, setAuctionBidsLoading] = useState(false);
  const [auctionBidsRefreshing, setAuctionBidsRefreshing] = useState(false);
  const [auctionBidsError, setAuctionBidsError] = useState("");
  const redirectTimerRef = useRef(null);
  const hasSubmittedAuctionOfferRef = useRef(false);

  const carId = useMemo(() => {
    const id = Number(car?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [car?.id]);

  const currentUserId = useMemo(
    () => resolveCurrentUserId(currentUser, offer),
    [currentUser, offer]
  );

  const shouldCheckAuction = useMemo(() => {
    const isAuction = Number(car?.is_auction ?? car?.isAuction ?? 0) === 1 || car?.is_auction === true;
    return Boolean(carId && isAuction);
  }, [car?.isAuction, car?.is_auction, carId]);

  const handleUpdateOffer = useCallback(
    (...args) => {
      if (typeof onUpdateOffer === "function") {
        onUpdateOffer(...args);
      }
      setRefreshKey((key) => key + 1);
    },
    [onUpdateOffer]
  );

  const refreshAuctionView = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  const handleAuctionOfferSubmitted = useCallback(() => {
    hasSubmittedAuctionOfferRef.current = true;
    setHasSubmittedAuctionOffer(true);
    setShowAuctionTab(true);
    setRefreshKey((key) => key + 1);

    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = window.setTimeout(() => {
      setActiveTab("auction");
      setRefreshKey((key) => key + 1);
    }, 3000);
  }, []);

  const canShowAuctionTab = showAuctionTab || hasSubmittedAuctionOffer;
  const shouldWaitForAuctionSnapshot = shouldCheckAuction && !auctionSnapshotLoaded;

  useEffect(() => {
    setAuctionSnapshotLoaded(false);
  }, [carId, isOpen, shouldCheckAuction]);

  useEffect(() => {
    if (!isOpen || !shouldCheckAuction) {
      setShowAuctionTab(false);
      setAuctionSnapshotLoaded(false);
      setHasSubmittedAuctionOffer(false);
      hasSubmittedAuctionOfferRef.current = false;
      setActiveTab("offer");
      setAuctionStatusSnapshot(null);
      setAuctionBidsError("");
      setAuctionBidsState({
        bids: [],
        highestBid: null,
        totalBids: 0,
        currentUserPosition: null,
      });
      return undefined;
    }

    let cancelled = false;
    let didInitialLoad = false;

    const fetchAuctionSnapshot = async () => {
      const silent = didInitialLoad;
      if (silent) {
        setAuctionBidsRefreshing(true);
      } else {
        setAuctionBidsLoading(true);
      }
      setAuctionBidsError("");

      const [statusResult, bidsResult] = await Promise.all([
        getAuctionStatus(carId, currentUserId),
        getAuctionBids(carId, currentUserId),
      ]);
      if (cancelled) return;

      const canShowAuction = statusResult.success && isVisibleAuctionPayload(statusResult.data);
      setAuctionStatusSnapshot(statusResult.success ? statusResult.data : null);
      setAuctionSnapshotLoaded(true);
      setShowAuctionTab(canShowAuction);

      if (!canShowAuction && !hasSubmittedAuctionOfferRef.current) {
        setActiveTab("offer");
      }

      if (bidsResult.success && bidsResult.data?.success !== false) {
        setAuctionBidsState(normalizeBidsPayload(bidsResult.data));
      } else {
        setAuctionBidsError(bidsResult.error || bidsResult.data?.error || "שגיאה בטעינת הצעות המכרז");
      }

      didInitialLoad = true;
      setAuctionBidsLoading(false);
      setAuctionBidsRefreshing(false);
    };

    fetchAuctionSnapshot();
    const intervalId = window.setInterval(fetchAuctionSnapshot, 7000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [carId, currentUserId, isOpen, refreshKey, shouldCheckAuction]);

  useEffect(() => () => {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000011] flex items-center justify-center bg-black/50 p-2 sm:p-4"
      dir="rtl"
      onClick={onClose}
      style={{backgroundColor:'black'}}
    >
      {/* Card */}
      <div
        className="
          relative w-full max-w-[360px] h-[96vh]
          rounded-[20px] bg-white shadow-xl border border-[#d9d9d9]
          overflow-hidden
        "
        dir="ltr"
        style={{backgroundColor: "white"}}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center px-4 pt-3 pb-2 border-b border-[#f0f0f0]">
          <button
            type="button"
            onClick={onClose}
            aria-label="סגור"
            className="
              absolute left-3 top-3
              h-7 w-7 rounded-full
              flex items-center justify-center
              text-[#a3a3a3] hover:bg-gray-100
            "
          >
            x
          </button>

          {canShowAuctionTab && (
            <div className="flex rounded-full border border-[#E5E7EB] bg-[#F8FAFC] p-1" dir="rtl">
              {[
                { key: "offer", label: "הגשת הצעה" },
                { key: "auction", label: "צפייה במכרז" },
              ].map((tab) => {
                const selected = activeTab === tab.key;

                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`h-8 min-w-[112px] rounded-full px-3 text-[13px] font-bold transition ${
                      selected ? "bg-black text-white" : "text-[#4B5563]"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="h-[calc(96vh-56px)] overflow-y-auto px-2 pb-2">
          {activeTab === "auction" && canShowAuctionTab ? (
            <MobileAuctionView
              state={auctionBidsState}
              loading={auctionBidsLoading}
              refreshing={auctionBidsRefreshing}
              error={auctionBidsError}
              auctionStatus={auctionStatusSnapshot}
              onAuctionStatusUpdate={refreshAuctionView}
              showCurrentUserLabel={Number(currentUser?.user_type ?? currentUser?.userType ?? 0) !== 2}
            />
          ) : shouldWaitForAuctionSnapshot ? (
            <div className="flex h-full min-h-[420px] items-center justify-center px-6" dir="rtl">
              <div className="w-full rounded-[20px] border border-[#E7EAF0] bg-white p-5 text-center shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
                <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-4 border-[#E5E7EB] border-t-[#CE0000]" />
                <div className="text-[15px] font-bold text-[#111827]">טוענים מצב מכרז</div>
                <div className="mt-1 text-[13px] font-medium text-[#64748B]">רגע אחד...</div>
              </div>
            </div>
          ) : (
            <SendNewOfferForm
              isRejectedOrApproved={isRejectedOrApproved}
              car={car}
              currentUser={currentUser}
              offer={offer}
              onUpdateOffer={handleUpdateOffer}
              onClose={onClose}
              variant="panel"
              auctionStatusOverride={auctionStatusSnapshot}
              onAuctionOfferSubmitted={handleAuctionOfferSubmitted}
            />
          )}
        </div>
      </div>
    </div>
  );
}
